import React, { useState, useRef, useEffect } from 'react'
import { useTheme, type ThemeMode } from '../../../contexts/ThemeContext'

const SunIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="5"/>
    <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/>
  </svg>
)

const MoonIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
  </svg>
)

const MonitorIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
    <line x1="8" y1="21" x2="16" y2="21"/>
    <line x1="12" y1="17" x2="12" y2="21"/>
  </svg>
)

const themes: Array<{ mode: ThemeMode; label: string; icon: React.ReactNode }> = [
  { mode: 'light', label: 'Light', icon: <SunIcon /> },
  { mode: 'dark', label: 'Dark', icon: <MoonIcon /> },
  { mode: 'system', label: 'System', icon: <MonitorIcon /> },
]

export function ThemeToggle() {
  const { mode, resolvedTheme, setMode } = useTheme()
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const currentIcon = resolvedTheme === 'light' ? <SunIcon /> : <MoonIcon />
  const currentTheme = themes.find(theme => theme.mode === mode) || themes[2]

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      return () => document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen])

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-center w-11 h-11 md:w-10 md:h-10
                   text-neutral-500 dark:text-neutral-400
                   hover:text-black dark:hover:text-white
                   focus:outline-none
                   transition-all duration-200"
        aria-label={`Current theme: ${currentTheme.label}. Click to change theme.`}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span className="transition-transform duration-200 hover:scale-110">
          {currentIcon}
        </span>
      </button>

      {isOpen && (
        <div
          className="absolute right-0 mt-2 py-2 w-36
                     bg-white dark:bg-neutral-900
                     border border-neutral-200 dark:border-neutral-800
                     rounded-[5px] z-50
                     animate-fade-in"
          role="listbox"
        >
          {themes.map((theme) => (
            <button
              key={theme.mode}
              onClick={() => {
                setMode(theme.mode)
                setIsOpen(false)
              }}
              className={`w-full flex items-center gap-3 px-4 py-2 text-sm text-left
                         hover:bg-neutral-100 dark:hover:bg-neutral-800
                         focus:outline-none focus:bg-neutral-100 dark:focus:bg-neutral-800
                         transition-colors duration-150 ${
                           mode === theme.mode
                             ? 'text-black dark:text-white bg-neutral-50 dark:bg-neutral-800/50'
                             : 'text-neutral-600 dark:text-neutral-400'
                         }`}
              role="option"
              aria-selected={mode === theme.mode}
            >
              <span className="text-neutral-500 dark:text-neutral-400">
                {theme.icon}
              </span>
              <span className="font-mono text-xs">{theme.label}</span>
              {mode === theme.mode && (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="ml-auto">
                  <polyline points="20,6 9,17 4,12"/>
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
